import { pmsReportFileName } from '@felix/domain';
import { browserPdfOutput, type PdfOutput, renderPdf } from '../personnel/pdf.js';
import { zoneOffsetMinutes } from '../personnel/rest-hours.js';
import { vesselTime } from '../personnel/ship-clock.js';
import {
  type PmsReportView,
  pmsReportDocument,
  type ReportPrint,
  type ReportT,
} from './report-document.js';
import { pmsReportPdf } from './report-pdf.js';

/** Basım bilgisinin kaynağı (`PmsSource.reportPrint`): sürüm, basan kişi ve rolü, marka. */
export interface ReportPrintInfo {
  version: string;
  name: string;
  role: string | null;
  brand: string | null;
}

export interface ReportPrintSource {
  reportPrint?(): Promise<ReportPrintInfo>;
  /** PDF'in kaydı: gemide kayıt penceresi; yoksa tarayıcı indirmesi. */
  pdf?: PdfOutput;
  /** Basım bilgisi yoksa basanın adı. */
  userName?: string;
}

/**
 * Basım bilgisi [NR496 4.1.2]: yazılım sürümü, basım anı, basan kişi ve rolü, geminin o anki saat
 * farkı (altlıkta gemi saati), şirket markası (ADR 0019 §4).
 */
export async function reportPrintOf(
  source: ReportPrintSource,
  data: Pick<PmsReportView, 'vessel'>,
  locale: string,
  now: () => Date = () => new Date(),
): Promise<ReportPrint> {
  const info = source.reportPrint ? await source.reportPrint().catch(() => null) : null;
  const at = now();
  return {
    version: info?.version || '0',
    printedAt: at.toISOString(),
    printedBy: { name: info?.name || source.userName || '—', role: info?.role ?? null },
    // Gemi saati kaydından (ADR 0065 §4)
    tzOffsetMin: zoneOffsetMinutes(vesselTime(data.vessel), at.getTime()),
    brand: info?.brand ?? null,
    locale,
  };
}

/**
 * Raporun PDF'i istemcide üretilir ve kaydedilir (plan M7 Task 31): pdfmake ve gömülü yazı tipi,
 * internetsiz (mimari belge §11.3). Dosya adı `felix-manager-<sürüm>-<rapor>-<gemi günü>.pdf`.
 */
export async function savePmsReportPdf(
  source: ReportPrintSource,
  data: PmsReportView,
  t: ReportT,
  locale: string,
  now: () => Date = () => new Date(),
): Promise<void> {
  const print = await reportPrintOf(source, data, locale, now);
  const definition = pmsReportPdf(pmsReportDocument(data, print, t));
  const output = source.pdf ?? browserPdfOutput();
  await output.save(pmsReportFileName(print.version, data.id, data.asOf, 'pdf'), () =>
    renderPdf(definition),
  );
}
